import { WebRtcPeer } from 'kurento-utils';
import { KurentoClient } from 'kurento-client';
import { kurentoConfig } from './KurentoClient/KurentoConfig';
import { BaseCameraStream } from './BaseCameraStream';
import { store } from 'store/store';
import { webRtcTelemetry } from 'services/StreamingObservability/WebRtcTelemetryService';
import { webRtcEndpointRegistry } from './WebRtcEndpointRegistry';
import { formatNackDecisionLine, getNackDecision, stripNackFromSdp } from 'utils/kurentoNack';
import { selectKurentoNackConfig } from 'store/featureFlag/featureFlagSelector';
import { selectCurrentDesk } from 'store/desksSlice/desksSelectors';

export default class CameraStreamConnection extends BaseCameraStream {
    mediaPipelineId;

    #webRTCPeer;
    #mediaPipeline;
    webRTCEndpoint;

    #streamTypeConfiguration;

    #onMediaStream;
    #onReactivateStream;
    #updateStreamState;
    #getTelemetryContext;
    #getNackTarget;
    #nackDisabled = false;

    fullHeader;

    #recoverDelayMilis = 60000;
    #maxRecoverRetries = 2;
    #recoverCount;

    //broken WebRTCEndpoint reference (KMS 40101) recovery
    #brokenReferenceRecoveryInProgress = false; // storm collapser: one recovery at a time
    #brokenReferenceRecoverCount = 0; // consecutive broken-ref recreates (reset on healthy connect)
    #maxBrokenReferenceRecovers = 2; // soft-retry budget; matches #maxRecoverRetries convention

    //promises (controlled outside)
    activationPromise;
    mediaStreamPromise;
    connectedPromise;
    #resolveConnectedPromise;
    #rejectConnectedPromise;
    localIceCandidatesUsed = [];
    mediaStream;

    //state
    #activating;
    live;
    recreating;
    #forceStop;
    #isCameraStreamConnectionInvalidated;
    #autoRecoverTimeout;
    constructor(
        kurentoClientId,
        onMediaStream,
        onReactivateStream,
        updateStreamState,
        camConfig,
        getTelemetryContext = () => ({}),
        getNackTarget = () => ({})
    ) {
        super();
        if (!CameraStreamConnection.tabUid) CameraStreamConnection.tabUid = store?.getState?.()?.transactions?.sharedWorker?.uid;
        if (!CameraStreamConnection.username) CameraStreamConnection.username = store?.getState?.()?.auth?.user?.username;
        const {
            camId,
            videoData: { playerEndpointId },
        } = camConfig;
        this.cameraId = camId;
        this.kurentoId = kurentoClientId;
        this.mediaPipelineId = playerEndpointId ? playerEndpointId.split('/').at(0) : undefined;
        this.#onMediaStream = onMediaStream;
        this.#onReactivateStream = onReactivateStream;
        this.#updateStreamState = updateStreamState;
        this.#getTelemetryContext = getTelemetryContext;
        this.#getNackTarget = getNackTarget;
        this.updateLogHeader();
    }

    /////////////////////////////  API   //////////////////////////////////////

    getPeerConnection() {
        return this.#webRTCPeer?.peerConnection || null;
    }

    async create(streamType) {
        if (this.live || this.#activating) {
            this.log(`create(): Already started or starting is in progress | exiting`);
            return null;
        }
        try {
            this.#activating = true;
            this.#forceStop = false;
            this.connectedPromise = new Promise((res, rej) => {
                this.#resolveConnectedPromise = res;
                this.#rejectConnectedPromise = rej;
            });
            this.#recordTelemetry({ eventType: 'webrtc_connection_create_started' });

            const [offer, mediaPipeline] = await Promise.all([this.#createWebRTCPeer(), this.getMediaObjectById(this.mediaPipelineId)]);

            this.log(`create(): created offer and media pipeline`);

            this.#mediaPipeline = mediaPipeline;

            await this.#createWebRTCEndpoint(streamType, offer);
            this.log(`create(): created webRTCEndpoint`);

            this.#activating = false;
            this.live = true;

            // in case command for releasing connection came while connection creation was in progress
            if (this.#forceStop) {
                this.log(`create(): initiating release`);
                await this.release();
                this.#forceStop = false;
            }

            return this.webRTCEndpoint;
        } catch (err) {
            this.#recordTelemetry({
                eventType: 'webrtc_connection_create_failed',
                failureReason: err?.name || err?.code || 'webrtc_connection_create_failed',
            });
            this.#rejectConnectedPromise(err.message);
            this.connectedPromise = null;
            this.activationPromise = null;
            this.handle(err, 'create()');
        }
    }

    async release(forceStop = false) {
        this.#recordTelemetry({ eventType: 'webrtc_connection_release_requested', outcome: 'release_requested' }, { immediate: true });
        this.#onMediaStream(null);
        this.activationPromise = null;
        this.mediaStreamPromise = null;
        clearTimeout(this.#autoRecoverTimeout);
        this.#autoRecoverTimeout = undefined;

        if (forceStop) {
            this.#forceStop = true;
        }

        try {
            if (this.#webRTCPeer) {
                await this.#webRTCPeer.dispose();
                this.#webRTCPeer = null;
                this.log(`release(): disposed WebRTCPeer`);
            }
        } catch (err) {
            this.handle(err, 'release()|webRtcPeer dispose');
        }

        try {
            if (this.webRTCEndpoint) {
                const endpointId = this.webRTCEndpoint.id;
                this.log(`release(): releasing webRTCEndpoint id=${endpointId}`);
                try {
                    await this.webRTCEndpoint.release();
                    this.log(`release(): released webRTCEndpoint id=${endpointId}`);
                } catch (err) {
                    if (this.#isMediaObjectNotFoundError(err)) {
                        this.log(`release(): webRTCEndpoint id=${endpointId} not found on KMS (40101), treating as already released`, 'warn');
                    } else {
                        throw err;
                    }
                }
                await webRtcEndpointRegistry.remove(endpointId);
                this.webRTCEndpoint = null;
            }
        } catch (err) {
            this.handle(err, 'release()|webRTCEndpoint release');
        }
        this.#streamTypeConfiguration = '';
        this.connectedPromise = null;
        this.#mediaPipeline = null;
        this.#activating = false;
        this.live = false;
    }

    updateKurentoId(id) {
        const updated = super.updateKurentoId(id);
        if (updated) this.updateStreamInfoMessage = `Changing kurento server. Waiting for kurento id=${this.kurentoId} connection...`;
    }

    async update(mediaPipelineId, streamType, forceUpdate = false) {
        try {
            if (mediaPipelineId === this.mediaPipelineId && !forceUpdate) {
                this.log(`update(): Same MediaPipelineId | exiting`);
                return;
            }
            if (!forceUpdate) {
                this.#updateStreamState({ info: this.updateStreamInfoMessage || 'Hard refresh in progress...' });
                this.updateStreamInfoMessage = '';
            }

            await this.release(true);
            this.mediaPipelineId = mediaPipelineId;
            await (this.activationPromise = this.create(streamType));
            !forceUpdate && this.log(`update(): updated CameraStreamConnection to new config mediaPipelineId=${this.mediaPipelineId}`);
        } catch (err) {
            this.handle(err, 'update()');
        }
    }

    async recreate(recover) {
        try {
            if (recover) {
                this.recreating = true;
                this.log(`recreate(): Trying to recover, attempt #${this.#recoverCount + 1}`);
                this.#updateStreamState({ info: `Stream auto recover initiated... attempt #${this.#recoverCount + 1}` });
            } else if (this.recreating) {
                this.log(`recreate(): Recreation already in progress, exiting`);
                return;
            }

            await this.update(this.mediaPipelineId, this.#streamTypeConfiguration, true);
            this.log(`recreate():Successfully recreated CameraStreamConnection with same config`);
        } catch (err) {
            ++this.#recoverCount;
            const retryOverflow = this.#recoverCount >= this.#maxRecoverRetries;
            recover && !retryOverflow && (await this.recreate(true));
            if (!recover || retryOverflow) {
                this.recreating = false;
                this.handle(err, 'recreate()');
            }
        }
        this.recreating = false;
        return this.webRTCEndpoint;
    }

    async configureWebRTCEndpointForStreamType(streamType) {
        try {
            if (!streamType || typeof streamType !== 'string') {
                this.log(`configureWebRTCEndpointForStreamType(): no type provided, exiting`);
                return;
            }
            const isSupported = Object.keys(VIDEO_CONFIG).includes(streamType);

            if (!isSupported) {
                this.log(
                    `configureWebRTCEndpointForStreamType(): No custom configuration for streamType=${streamType}, currently active configuration streamType=${
                        this.#streamTypeConfiguration
                    } will not change , exiting`
                );
                return;
            }
            this.addWebRtcEndpointTag('activeStream', streamType);
            if (this.#streamTypeConfiguration === streamType) {
                this.log(`configureWebRTCEndpointForStreamType(): Already configured for streamType=${streamType}, exiting`);
                return;
            }

            const bitrates = VIDEO_CONFIG[streamType].bitrates;
            const minVideoKbps = bitrates.minVideoKbps;
            const maxVideoKbps = bitrates.maxVideoKbps;
            const minOutputBitrate = (minVideoKbps + bitrates.minAudioKbps) * 1000;
            const maxOutputBitrate = (maxVideoKbps + bitrates.maxAudioKbps) * 1000;

            // must set maxes before mins, so Kurento accepts min > previous max
            await Promise.all([
                this.configureMediaObject(this.webRTCEndpoint, 'setMaxOutputBitrate', maxOutputBitrate),
                this.configureMediaObject(this.webRTCEndpoint, 'setMaxVideoSendBandwidth', maxVideoKbps),
            ]);

            await Promise.all([
                this.configureMediaObject(this.webRTCEndpoint, 'setMinOutputBitrate', minOutputBitrate),
                this.configureMediaObject(this.webRTCEndpoint, 'setMinVideoSendBandwidth', minVideoKbps),
            ]);

            this.#streamTypeConfiguration = streamType;
        } catch (err) {
            this.handle(err, 'configureWebRTCEndpointForStreamType()');
        }
    }

    async addWebRtcEndpointTag(tag, value) {
        try {
            if (!allowedWebRtcEndpointTags.includes(tag)) return;
            if (this.#activating) await this.activationPromise;
            if (!this.webRTCEndpoint) return;
            await this.addTagToMediaObject(this.webRTCEndpoint, tag, value); //
        } catch (err) {
            this.handle(err, 'addTag()');
        }
    }

    isConnected() {
        return this.#webRTCPeer?.peerConnection?.connectionState === 'connected';
    }

    async getWebRTCEndpoint(recover) {
        let webRTCEndpoint = null;
        try {
            if (this.webRTCEndpoint) {
                try {
                    await this.getMediaObjectCreationTime(this.webRTCEndpoint);
                    webRTCEndpoint = this.webRTCEndpoint;
                } catch {
                    try {
                        await this.getMediaObjectCreationTime(this.#mediaPipeline);
                    } catch {
                        throw new Error('Media Pipeline not found', { cause: 'MediaPipeline' });
                    }
                    throw new Error('WebRTCEndpoint not found');
                }

                if (recover) webRTCEndpoint = await this.recoverConnection();
            }
        } catch (err) {
            const rethrow = err.cause === 'MediaPipeline' || err.cause === 'RequestFailed';

            if (rethrow) this.#handleAutoRecoverFailed(err);
            this.handle(err, 'getWebRTCEndpoint()', rethrow);

            webRTCEndpoint = await this.recoverConnection(); //hardRefresh
        }

        return webRTCEndpoint;
    }

    async recoverConnection(hardRefresh) {
        try {
            if (hardRefresh) {
                this.#recordTelemetry({
                    eventType: 'hard_refresh_required',
                    hardRefreshAttempted: true,
                    hardRefreshOutcome: 'requested',
                });
                this.#updateStreamState({ action: 'hardRefresh' });
                return;
            }
            this.#recoverCount = 0;
            return await this.recreate(true); //try softrefresh for maxretries times
        } catch (err) {
            this.#handleAutoRecoverFailed(err);
            return;
        }
    }

    async #handleAutoRecoverFailed(err) {
        this.#updateStreamState({
            error: `Auto recover failed
            [${err.message}]
            Please try hard refresh!`,
        });
        await this.release();
    }

    async #recoverFromBrokenWebRtcEndpoint(source) {
        if (this.#brokenReferenceRecoveryInProgress) return; // collapse the error storm
        if (this.recreating) return; // an autoRecover/soft-refresh recreate already owns it
        if (this.#isCameraStreamConnectionInvalidated) return;
        if (!this.live && !this.#activating) return; // already released

        this.#brokenReferenceRecoveryInProgress = true; // set synchronously before any await (atomic)
        try {
            if (this.#activating && this.activationPromise) {
                try {
                    await this.activationPromise;
                    // eslint-disable-next-line
                } catch (_) {
                    /* create() already ran its own error handling */
                }
            }
            if (this.#isCameraStreamConnectionInvalidated || (!this.live && !this.#activating)) return;

            if (this.#brokenReferenceRecoverCount >= this.#maxBrokenReferenceRecovers) {
                this.log(`#recoverFromBrokenWebRtcEndpoint(): exhausted ${this.#brokenReferenceRecoverCount} soft attempts, giving up`, 'error');
                await this.#handleAutoRecoverFailed(new Error('WebRTCEndpoint reference broken (recovery exhausted)'));
                return;
            }

            this.#brokenReferenceRecoverCount++;
            console.warn(
                `${this.fullHeader}#recoverFromBrokenWebRtcEndpoint(): broken webRTCEndpoint id=${this.webRTCEndpoint?.id} (source=${source}, attempt=${this.#brokenReferenceRecoverCount})`
            );

            const webRTCEndpoint = await this.recoverConnection(); // soft only (no hard-refresh escalation)

            if (this.#isCameraStreamConnectionInvalidated || (!this.live && !this.#activating)) return;

            if (webRTCEndpoint) {
                console.log(`${this.fullHeader} #recoverFromBrokenWebRtcEndpoint(): webRtcEndpoint recovered`);
                this.#onReactivateStream(); // reconnect PlayerEndpoint to the fresh endpoint
            }
        } finally {
            this.#brokenReferenceRecoveryInProgress = false;
        }
    }

    ////////////////////////////   PRIVATE  //////////////////////////////////////////
    async #createWebRTCPeer() {
        const options = {
            configuration: kurentoConfig.getOptionsByKurentoId(this.kurentoId),
        };

        try {
            this.#webRTCPeer = WebRtcPeer.WebRtcPeerRecvonly(options);
            this.log(`#createWebRTCPeer():created webRTCPeer`);
            this.#setWebRTCPeerListeners();
            this.#recordTelemetry({ eventType: 'webrtc_peer_created' });

            // decided once per connection, so the offer and the answer in #processOffer always agree
            this.#nackDisabled = this.#resolveNackDisabled();
            const offer = this.#nackDisabled ? await this.#generateOfferWithoutNack() : await this.#generateOffer();

            this.log(`#createWebRTCPeer():returning offer`);
            return offer;
        } catch (err) {
            this.handle(err, '#createWebRTCPeer()', 'Error creating WebRTCPeer');
        }
    }

    #generateOffer() {
        return new Promise((res, rej) => {
            this.#webRTCPeer.generateOffer((err, offer) => {
                if (err) {
                    rej(err);
                    return;
                }
                this.log(`#createWebRTCPeer():generated offer`);
                res(offer);
            });
        });
    }

    async #generateOfferWithoutNack() {
        const pc = this.#webRTCPeer.peerConnection;
        pc.addTransceiver('video', { direction: 'recvonly' });
        const rawOffer = await pc.createOffer();
        try {
            await pc.setLocalDescription({ type: 'offer', sdp: stripNackFromSdp(rawOffer.sdp) });
            console.log(`${this.fullHeader} #createWebRTCPeer():generated offer without NACK`);
        } catch (err) {
            // the browser refused the stripped SDP: keep the video and lose the NACK optimisation. The pc stays
            // "stable" after a rejected setLocalDescription, so the unmodified offer can be applied right away.
            this.#nackDisabled = false;
            console.warn(`${this.fullHeader} [WEBRTC NACK CONFIG] decision="fallback" reason="${err?.message || err}"`);
            await pc.setLocalDescription(rawOffer);
        }
        return pc.localDescription.sdp;
    }

    async #createWebRTCEndpoint(streamType, offer) {
        try {
            this.webRTCEndpoint = await this.createMediaPipelineElement(this.#mediaPipeline);

            if (!CameraStreamConnection.tabUid) CameraStreamConnection.tabUid = store?.getState?.()?.transactions?.sharedWorker?.uid;

            await webRtcEndpointRegistry.add({
                webrtcEndpointId: this.webRTCEndpoint?.id,
                kurentoId: this.kurentoId,
                tabId: CameraStreamConnection.tabUid,
            });
            this.log(`#createWebRTCEndpoint(): created webRTCEndpoint id=${this.webRTCEndpoint?.id}`);
            // this.configureWebRTCEndpointForStreamType(streamType);
            this.#setWebRTCEndpointListeners();
            this.#setIceCandidateCallbacks();
            await this.#processOffer(offer);
            this.addWebRtcEndpointTag('username', CameraStreamConnection.username);
            CameraStreamConnection.tabUid && this.addWebRtcEndpointTag('tabUid', CameraStreamConnection.tabUid);

            this.log(`#createWebRTCEndpoint(): processed offer`);
        } catch (err) {
            this.handle(err, '#createWebRtcEndpoint()', 'Error creating WebRTCEndpoint');
        }
    }

    #autoRecover = () => {
        this.#autoRecoverTimeout = undefined;
        this.log(`#autoRecover(): Initiating auto recover, current webRTCEndpointId=${this.webRTCEndpoint?.id}`);
        if (!this.live && !this.#activating) {
            this.log(`#autoRecover(): connection already released, skipping`);
            return;
        }
        if (this.#isCameraStreamConnectionInvalidated) {
            this.log(`#autoRecover(): Camera stream connection invalidated, exiting`);
            this.#updateStreamState({ error: 'Stream configuration invalidated, please try hard refresh!' });
            return;
        }
        if (this.#brokenReferenceRecoveryInProgress) {
            this.log(`#autoRecover(): broken-reference recovery already in progress, exiting`);
            return;
        }
        this.#updateStreamState({ info: 'Stream auto recover initiated...' });
        this.getWebRTCEndpoint(true).then((webRTCEndpoint) => {
            if (!this.live && !this.#activating) {
                this.log(`#autoRecover(): connection released while recovering, aborting reactivation`);
                return;
            }
            webRTCEndpoint && this.#onReactivateStream();
        });
    };

    #setWebRTCPeerListeners() {
        const peerConnection = this.#webRTCPeer.peerConnection;

        peerConnection.addEventListener('connectionstatechange', () => {
            this.#recordTelemetry({
                eventType: 'peer_connection_state_change',
                failureReason: failedState(peerConnection.connectionState) ? `peer_connection_${peerConnection.connectionState}` : undefined,
            });
        });

        peerConnection.addEventListener('icegatheringstatechange', () => {
            this.#recordTelemetry({ eventType: 'ice_gathering_state_change' });
        });

        peerConnection.addEventListener('signalingstatechange', () => {
            this.#recordTelemetry({ eventType: 'signaling_state_change' });
        });

        peerConnection.addEventListener('iceconnectionstatechange', () => {
            const connectionState = peerConnection.iceConnectionState;

            this.log(`WebRTCPeer event - ICEconnectionstatechange=${connectionState} `);
            this.#recordTelemetry({
                eventType: 'ice_connection_state_change',
                failureReason: failedState(connectionState) ? `ice_${connectionState}` : undefined,
            });
            if (connectionState === 'checking') return;

            if (connectionState === 'disconnected' || connectionState === 'failed' || connectionState === 'closed') {
                if (this.connectedPromise) {
                    this.log(`Rejecting connectedPromise due connection failed`, 'error');
                    this.#rejectConnectedPromise('Connection failed');
                    this.connectedPromise = null;
                    this.activationPromise = null;
                    this.#autoRecover();
                    return;
                }

                if (this.recreating) {
                    this.log(`WebRTCPeer event - ICEconnectionstatechange auto recover already initiated, exiting`);
                } else if (!this.#autoRecoverTimeout) {
                    this.log(
                        `WebRTCPeer event - ICEconnectionstatechange=${connectionState}, scheduling stream autorecover in ${
                            this.#recoverDelayMilis
                        } ms`
                    );
                    this.#autoRecoverTimeout = setTimeout(this.#autoRecover, this.#recoverDelayMilis);
                }
            } else {
                if (connectionState === 'connected' || connectionState === 'completed') this.#brokenReferenceRecoverCount = 0;
                if (this.connectedPromise) {
                    this.#resolveConnectedPromise();
                    this.log('Resolved connectedPromise');
                    this.connectedPromise = null;
                    this.#updateStreamState({ info: 'Connected, waiting for stream...', isLoading: true });
                }
                if (this.#autoRecoverTimeout) {
                    this.log(`WebRTCPeer event - ICEconnectionstatechange=${connectionState}, canceling autorecover.`);
                    clearTimeout(this.#autoRecoverTimeout);
                    this.#autoRecoverTimeout = undefined;
                }
            }
        });

        peerConnection.addEventListener('icecandidateerror', (event) => {
            this.log(`WebRTCPeer event - [icecandidateerror] -> ${event.errorText} [url] -> ${event.url}`, 'warn');
            this.#recordTelemetry({
                eventType: 'ice_candidate_error',
                failureReason: `ice_candidate_error_${event.errorCode || 'unknown'}`,
            });
        });

        this.mediaStreamPromise = new Promise((res) => {
            peerConnection.onaddstream = (e) => {
                this.log(`WebRTCPeer event - created media stream`);
                this.#recordTelemetry({ eventType: 'media_stream_received', outcome: 'remote_stream_added' });
                this.#onMediaStream(e.stream);
                res(e.stream);
            };
        });
    }

    #setWebRTCEndpointListeners() {
        this.webRTCEndpoint.on('Error', (event) => {
            const objectGone = event?.errorCode === 40101 || event?.type === 'MEDIA_OBJECT_NOT_FOUND';
            this.log(`WebRTCEndpoint event - Error [${event?.errorCode}] ${event?.description || event?.type}`, objectGone ? 'warn' : 'error');
            if (objectGone) this.#handleBrokenWebRtcEndpoint('WebRTCEndpoint Error event');
        });

        this.webRTCEndpoint.on('IceComponentStateChange', (e) => {
            this.log(`WebRTCEndpoint event - IceComponentStateChange ${e.state}`);
            this.#recordTelemetry({
                eventType: 'kms_webrtc_ice_component_state',
                failureReason: failedState(e.state) ? `kms_ice_${e.state}` : undefined,
            });
        });

        this.webRTCEndpoint.on('IceGatheringDone', () => {
            this.log(`WebRTCEndpoint event - IceGatheringDone`);
            this.#recordTelemetry({ eventType: 'kms_webrtc_ice_gathering_done' });
        });

        this.webRTCEndpoint.on('NewCandidatePairSelected', ({ candidatePair: { localCandidate, remoteCandidate } }) => {
            this.#extractIceCandidateIp(remoteCandidate); //client ICE candidate, this is from KMS perspective
            this.log(`WebRTCEndpoint event - NewCandidatePairSelected: WebRTCEndpoint ${localCandidate} | WebRTCPeer ${remoteCandidate} `);
            this.#recordTelemetry({ eventType: 'selected_candidate_pair_changed', candidatePairState: 'selected' });
        });
    }

    #setIceCandidateCallbacks() {
        this.#webRTCPeer.on('icecandidate', (candidate) => {
            this.log(`New LocalIceCandidate found, ${candidate?.candidate}`);
            candidate = KurentoClient.register.complexTypes.IceCandidate(candidate);
            this.webRTCEndpoint.addIceCandidate(candidate, (err) => this.#errorCallback(err, 'WebRTCEndpoint: Error adding ICE candidate'));
        });

        this.webRTCEndpoint.on('OnIceCandidate', (event) => {
            this.#webRTCPeer.addIceCandidate(event.candidate, (err) => this.#errorCallback(err, 'WebRTCPeer: Error adding ICE candidate'));
        });
    }

    // the offer already has NACK stripped when #nackDisabled (see #generateOfferWithoutNack)
    async #processOffer(offer) {
        try {
            const answer = await new Promise((resolve, reject) => {
                this.webRTCEndpoint.processOffer(offer, (err, sdpAnswer) => {
                    if (err) {
                        this.#recordTelemetry({ eventType: 'process_offer_failed', failureReason: err?.name || err?.code || 'process_offer_failed' });
                        console.log(
                            `Error processing SDP offer err=${err?.message || err?.code || err?.stack || String(err)} kurentoId=${this.kurentoId} cameraId=${this.cameraId} webRtcEndpointId=${this.webRTCEndpoint?.id} (type=${typeof offer}, len=${offer?.length ?? 0}) offer=${offer?.slice?.(0, 300)}`
                        );
                        return reject(err);
                    }
                    resolve(sdpAnswer);
                });
            });

            if (typeof answer !== 'string' || !/^\s*v=\d/.test(answer)) {
                this.#recordTelemetry({ eventType: 'invalid_sdp_answer', failureReason: 'invalid_sdp_answer' });
                console.log(
                    `Invalid SDP answer from kurentoId=${this.kurentoId} cameraId=${this.cameraId} webRtcEndpointId=${this.webRTCEndpoint?.id} (type=${typeof answer}, len=${answer?.length ?? 0}) answer=${answer?.slice?.(0, 300)} offer=${offer?.slice?.(0, 300)}`
                );
            }

            this.webRTCEndpoint.gatherCandidates((err) => this.#errorCallback(err, 'WebRTCEndpoint: Error gathering ICE candidates'));
            // Kurento cannot answer with NACK the offer did not contain; stripping the answer too is a safety net
            const answerToApply = this.#nackDisabled ? stripNackFromSdp(answer) : answer;
            await new Promise((res, rej) => this.#webRTCPeer.processAnswer(answerToApply, (err) => (err ? rej(err) : res())));
        } catch (err) {
            if (this.#isMediaObjectNotFoundError(err)) {
                this.#handleBrokenWebRtcEndpoint('#processOffer()');
                return;
            }
            this.handle(err, '#processOffer()');
        }
    }

    // A camera the config matches gets a [WEBRTC NACK CONFIG] line saying why NACK is disabled or kept (by an exclude).
    // Plain console.log, so it reaches AIQ /logs (Grafana) in production too. Cameras the config does not match log nothing.
    // Any unexpected error keeps NACK on: the config must never stop a stream from being created.
    #resolveNackDisabled() {
        try {
            const state = store?.getState?.();
            if (!state) return false;
            const nackTarget = this.#getNackTarget?.() || {};
            const target = { ...nackTarget, deskId: selectCurrentDesk(state)?.id, cameraId: nackTarget.cameraId ?? this.cameraId };
            const decision = getNackDecision(selectKurentoNackConfig(state), target);

            if (decision.matchedBy) {
                const line = formatNackDecisionLine(decision, {
                    deskId: target.deskId,
                    site: target.locationName,
                    gateId: target.gateId,
                    cameraId: target.cameraId,
                    cameraType: target.cameraType,
                    kurentoId: this.kurentoId,
                });
                console.log(`${this.fullHeader} ${line}`);
            }
            return decision.disabled;
        } catch (err) {
            console.warn(`${this.fullHeader} [WEBRTC NACK CONFIG] decision="error" reason="${err?.message || err}"`);
            return false;
        }
    }

    #onForceStop() {
        this.#forceStop = false;
        this.release();
    }

    //ERROR HANDLER

    #isMediaObjectNotFoundError(err) {
        if (!err) return false;
        const looksLikeNotFound = err.code === 40101 || err.name === 'ReferenceError';
        if (!looksLikeNotFound) return false;
        const endpointId = this.webRTCEndpoint?.id;
        if (endpointId && typeof err.message === 'string') return err.message.includes(endpointId);
        return true;
    }

    #handleBrokenWebRtcEndpoint(source) {
        if (this.#resolveConnectedPromise && this.connectedPromise) {
            this.#resolveConnectedPromise();
            this.connectedPromise = null;
        }
        this.#recoverFromBrokenWebRtcEndpoint(source).catch(() => {}); // guarded internally
    }

    #errorCallback(err, message, renderMessage) {
        if (!err) return;
        this.#recordTelemetry({ eventType: 'webrtc_operation_failed', failureReason: err?.name || err?.code || message });

        if (this.#isMediaObjectNotFoundError(err)) {
            this.#handleBrokenWebRtcEndpoint(message);
            return;
        }

        if (this.#rejectConnectedPromise) {
            this.#rejectConnectedPromise();
            this.connectedPromise = null;
            this.activationPromise = null;
        }
        this.handle(err, message, false);
        this.#updateStreamState({ info: '', error: renderMessage || message, isLoading: false });
    }

    handle(err, message, rethrow = true) {
        if (err) {
            this.log(`${message}: ${err?.message ?? err}`, 'error');

            if (this.#forceStop) {
                this.#onForceStop();
                return;
            }

            if (rethrow) {
                const rethrowMessage = typeof rethrow === 'string' ? rethrow : (err?.message ?? err);
                throw new Error(rethrowMessage);
            }
        }
    }

    #extractIceCandidateIp(candidate) {
        const hostnameMatch = /(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}/;
        const ipMatch = /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/;

        const hostnames = candidate.match(hostnameMatch);
        const ips = candidate.match(ipMatch);

        ips?.forEach((ip) => this.localIceCandidatesUsed.push(ip));
        hostnames?.forEach((hostname) => this.localIceCandidatesUsed.push(hostname));
    }

    setIsInvalidated(boolean) {
        this.#isCameraStreamConnectionInvalidated = boolean;
        if (boolean) this.webRTCEndpoint = null;
    }

    updateLogHeader() {
        this.fullHeader = `[CameraStreamConnection kurentoId=${this.kurentoId}, cameraId=${this.cameraId}]`;
    }

    #recordTelemetry(event, { peerConnection = this.#webRTCPeer?.peerConnection, immediate = false } = {}) {
        const context = this.#getTelemetryContext?.() || {};
        webRtcTelemetry.recordPeerConnectionEvent(
            {
                ...context,
                cameraId: this.cameraId,
                kurentoId: this.kurentoId,
                streamType: event.streamType || context.streamType || this.#streamTypeConfiguration,
                ...event,
            },
            peerConnection,
            { immediate }
        );
    }
}

function failedState(state) {
    return ['failed', 'disconnected', 'closed'].includes(String(state || '').toLowerCase());
}
const allowedWebRtcEndpointTags = ['username', 'tabUid'];

const VIDEO_CONFIG = {
    MAIN: {
        bitrates: {
            // minVideoKbps: 448,
            minVideoKbps: 1024,
            maxVideoKbps: 2048,
            minAudioKbps: 48,
            maxAudioKbps: 64,
        },
    },

    SUB: {
        bitrates: {
            minVideoKbps: 448,
            maxVideoKbps: 560,
            minAudioKbps: 48,
            maxAudioKbps: 64,
        },
    },
};
